import { api } from "@/lib/api";
import { localToday, permitProgress, type PermitDetail, type PermitEvent, type PermitStep } from "@/lib/permitProcesses";
import { danaDo, type Oznaka } from "@/lib/status";

export type HealthSafetyKind = "medical" | "training";
export interface SafetyProcess extends Omit<PermitDetail["process"], "process_type"> { process_type: HealthSafetyKind }
export interface SafetyDetail {
  process: SafetyProcess;
  steps: PermitStep[];
  events: (PermitEvent & { appointment_on: string | null; performed_on: string | null; institution: string | null })[];
  record_prefill: { radnik_id: string; record_type: string; datum_pregleda: string | null; datum_osposobljavanja: string | null } | null;
}
export const safetyPermission = (kind: HealthSafetyKind) => kind === "medical" ? "zdravlje" : "znr";
export const safetyTitle = (kind: HealthSafetyKind) => kind === "medical" ? "Liječnički pregled" : "Osposobljavanje ZNR";
export const safetyPath = (kind: HealthSafetyKind, id: string) => `/${safetyPermission(kind)}/postupci/${id}`;
export const safetyProgress = (detail: Pick<SafetyDetail, "process" | "steps">) => permitProgress(detail);

export async function getSafetyDetail(id: string, kind: HealthSafetyKind, signal?: AbortSignal) {
  const data = (await api.get<SafetyDetail>(`/processes/${id}/detail`, { params: { process_type: kind }, signal })).data;
  if (data.process.process_type !== kind) throw new Error("Vrsta postupka ne odgovara kartici.");
  return data;
}
export async function getSafetyProcesses(workerId: string, kind: HealthSafetyKind, signal?: AbortSignal) {
  const data = (await api.get<SafetyProcess[]>(`/radnici/${workerId}/processes`, { params: { process_type: kind }, signal })).data;
  if (!Array.isArray(data)) throw new Error("Postupci nisu dostupni.");
  return Promise.all(data.filter(item => item.process_type === kind).map(item => getSafetyDetail(item.id, kind, signal)));
}
export async function startSafetyProcess(workerId: string, kind: HealthSafetyKind) {
  const templates = (await api.get<{ id: string; process_type: string; version: number; status: string; valid_from: string; valid_to: string | null }[]>("/process-templates")).data;
  const today = localToday();
  const template = templates.filter(item => item.process_type === kind && item.status === "active" && item.valid_from <= today && (!item.valid_to || item.valid_to >= today)).sort((a, b) => b.version - a.version)[0];
  if (!template) throw new Error("Nema aktivnog predloška postupka.");
  return (await api.post<SafetyProcess>(`/radnici/${workerId}/processes`, { template_id: template.id })).data;
}
export interface OrganizationalInput { action: "referral_sent" | "appointment" | "performed" | "correction"; on: string; institution: string | null; remind_on: string | null }
export async function addSafetyEvent(id: string, body: OrganizationalInput) { await api.post(`/processes/${id}/zapisi`, body); }
export async function completeSafetyStep(id: string, evidence: { zdravstvena_sposobnost_id?: string; osposobljavanje_znr_id?: string } = {}) { await api.post(`/process-steps/${id}/complete`, evidence); }

/** Presentation thresholds from the approved H2 mockup; no legal interval. */
export function expiryBadge(iso?: string | null, missing = "Nema podatka"): Oznaka {
  if (!iso) return { ton: "idle", tekst: missing };
  const days = danaDo(iso, new Date());
  return { ton: days <= 14 ? "bad" : days <= 30 ? "warn" : "ok", tekst: days < 0 ? `Rok prošao prije ${-days} dana` : days === 0 ? "Rok je danas" : `Rok za ${days} dana` };
}
